import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Badge, Button, Empty, Input, Popover, Tooltip } from 'antd';
import { NavLink, useLocation, Link } from 'react-router-dom';
import {
  AppstoreOutlined,
  BellOutlined,
  DownOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from '@ant-design/icons';

import './styles/layout.scss';
import { getUser, getUserRoleForLayout, isFeatureEnabled, logout, getIsSuperAdmin } from '../../../features/auth.store';
import {
  fetchNotifications,
  getNotificationsPublishing,
  getUnreadNotificationCount,
  getVisibleNotifications,
  markAllNotificationsRead,
  publishNotification,
} from '../../../features/notifications.store';
import { useDispatch, useSelector } from 'react-redux';

// Notification endpoints are disabled in the backend; bell stays visible but inert.
const NOTIFICATIONS_ENABLED = false;

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const userData = useSelector(getUser);
  const userRoleForLayout = useSelector(getUserRoleForLayout);
  const getFeatureEnabled = useSelector(isFeatureEnabled);
  const isSuperAdmin = useSelector(getIsSuperAdmin);
  const orderedNotifications = useSelector(getVisibleNotifications);
  const unreadCount = useSelector(getUnreadNotificationCount);
  const notificationsPublishing = useSelector(getNotificationsPublishing);

  const featureBuild = getFeatureEnabled('view_build');
  const featureUsers = getFeatureEnabled('view_users');

  const [openGroup, setOpenGroup] = useState(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [announcementDraft, setAnnouncementDraft] = useState('');

  const handleLogout = async () => {
    await dispatch(logout());
  };

  const featureViewCompany = getFeatureEnabled('view_company');
  const profilePicture = String(userData?.user_profile_picture || '').trim();
  const hasProfilePicture = Boolean(profilePicture);

 const formatRoleName = (role) => {
  if (!role) return 'User';
  return String(role).replace(/\s+Role$/i, '').trim();
};

  const handleMarkAllNotificationsRead = () => {
    if (!unreadCount) return;
    dispatch(markAllNotificationsRead());
  };

  const handleNotificationOpenChange = (nextOpen) => {
    setIsNotificationOpen(nextOpen);
    if (nextOpen) {
      handleMarkAllNotificationsRead();
    }
  };

  const publishAnnouncement = async () => {
    const content = announcementDraft.trim();
    if (!content) return;

    const action = await dispatch(publishNotification({ message: content }));
    if (publishNotification.fulfilled.match(action)) {
      setAnnouncementDraft('');
    }
  };

  const notificationContent = (
    <div className="notification-panel">
      <div className="notification-panel-header d-flex ai-center jc-space-between">
        <div>
          <div className="notification-title">Notifications</div>
          <div className="notification-subtitle">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </div>
        </div>
        {!!orderedNotifications.length && (
          <button type="button" className="notification-link-btn" onClick={handleMarkAllNotificationsRead}>
            Mark all read
          </button>
        )}
      </div>

      {isSuperAdmin && (
        <div className="notification-compose">
          <div className="compose-label">Broadcast To Users</div>
          <Input.TextArea
            value={announcementDraft}
            onChange={(e) => setAnnouncementDraft(e.target.value)}
            placeholder="Type an announcement for all users"
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={240}
          />
          <div className="compose-actions d-flex jc-space-between ai-center">
            <span>{announcementDraft.length}/240</span>
            <Button
              type="primary"
              size="small"
              onClick={publishAnnouncement}
              loading={notificationsPublishing}
              disabled={!announcementDraft.trim() || notificationsPublishing}
            >
              Send
            </Button>
          </div>
        </div>
      )}

      <div className="notification-list">
        {orderedNotifications.length ? (
          orderedNotifications.map((item) => {
            const isRead = item.isRead;

            return (
              <div key={item.id} className={`notification-item ${isRead ? 'read' : 'unread'}`}>
                <div className="notification-item-head d-flex ai-center jc-space-between">
                  <span className="notification-item-title">{item.title}</span>
                  <span className="notification-item-date">{new Date(item.createdAt).toLocaleString()}</span>
                </div>
                <div className="notification-item-message">{item.message}</div>
                <div className="notification-item-author">By {item.author || 'System'}</div>
              </div>
            );
          })
        ) : (
          <div className="notification-empty">
            <Empty description="No notifications yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        )}
      </div>
    </div>
  );

  const clientsRoutes = useMemo(() => ['/companies', '/sites'], []);
  const dashboardRoutes = useMemo(() => ['/insights'], []);
  const reportsRoutes = useMemo(() => ['/reports/serviced', '/reports/after-service', '/reports/handover', '/reports/site-asr'], []);
  const buildRoutes = useMemo(
    () => ['/build', '/catalog', '/projects', '/production-planning', '/production-timeline', '/contractors'],
    []
  );

  const isAnyRouteActive = (routes) =>
    routes.some((r) => location.pathname === r || location.pathname.startsWith(r + '/'));

  const dashboardActive = isAnyRouteActive(dashboardRoutes);
  const clientsActive = isAnyRouteActive(clientsRoutes);
  const reportsActive = isAnyRouteActive(reportsRoutes);
  const buildActive = isAnyRouteActive(buildRoutes);
  const currentSection = location.pathname === '/insights'
    ? 'Overview'
    : location.pathname === '/sites'
    ? 'All sites'
    : location.pathname.split('/').filter(Boolean).pop()?.replace(/-/g, ' ') || 'Overview';

  useEffect(() => {
    if (clientsActive) setOpenGroup('clients');
    else if (featureBuild && buildActive) setOpenGroup('build');
    else if (reportsActive) setOpenGroup('reports');
  }, [clientsActive, buildActive, reportsActive, featureBuild]);

  useEffect(() => {
    if (!NOTIFICATIONS_ENABLED) return undefined;

    // Fetch notifications immediately on load
    if (userData?.sub) {
      dispatch(fetchNotifications());
    }

    // Set up polling interval to fetch notifications every 5 seconds
    const pollInterval = setInterval(() => {
      if (userData?.sub) {
        dispatch(fetchNotifications());
      }
    }, 5000);

    // Cleanup interval on unmount
    return () => clearInterval(pollInterval);
  }, [dispatch, userData?.sub]);

  const toggleGroup = (groupId) => {
    if (isSidebarCollapsed) {
      setIsSidebarCollapsed(false);
      setTimeout(() => {
        setOpenGroup(groupId);
      }, 180);
      return;
    }

    setOpenGroup((prev) => (prev === groupId ? null : groupId));
  };

  const toggleSidebar = () => {
    if (!isSidebarCollapsed) {
      setOpenGroup(null);
    }
    setIsSidebarCollapsed((prev) => !prev);
  };

  const renderGroup = ({ id, iconSrc, title, active, children: subMenu }) => {
    const expanded = openGroup === id && !isSidebarCollapsed;

    const content = (
      <div className={`menu-group ${active ? 'group-active' : ''}`} key={id}>
        <button
          type="button"
          className={`menu-item menu-item--group ${active ? 'active' : ''} ${expanded ? 'expanded' : ''}`}
          onClick={() => toggleGroup(id)}
          aria-expanded={expanded}
        >
          <img alt="i" src={iconSrc} className="icon" />
          <div className="title d-flex jc-space-between w-100">
            <span className="menu-text">{title}</span>
            <span className={`chev ${expanded ? 'open' : ''}`}>
              <DownOutlined />
            </span>
          </div>
        </button>

        <div className={`sub-menu-collapse ${expanded ? 'open' : ''}`}>
          <div className="sub-menu">{subMenu}</div>
        </div>
      </div>
    );

    if (isSidebarCollapsed) {
      return (
        <Tooltip placement="right" title={title} key={id}>
          {content}
        </Tooltip>
      );
    }

    return content;
  };

  const renderSingleMenu = ({ to, iconSrc, iconNode, title }) => {
    const item = (
      <NavLink to={to} className="menu-item" end>
        {iconNode ? <span className="icon icon-font">{iconNode}</span> : <img alt="i" src={iconSrc} className="icon" />}
        <div className="title">
          <span className="menu-text">{title}</span>
        </div>
      </NavLink>
    );

    if (isSidebarCollapsed) {
      return (
        <Tooltip placement="right" title={title} key={to}>
          {item}
        </Tooltip>
      );
    }

    return item;
  };

  return (
    <div className={`layout d-flex mulish ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <div className="body h-100 d-flex">
        <aside className={`side-bar h-100 d-flex f-col ${isSidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="side-bar-top">
            <div className="logo-window">
              <img className="logo" src="/images/MineARC_Logo_White.png" alt="MineARC Systems" />
            </div>
          </div>

          <div className="side-bar-menu d-flex f-col jc-space-between">
            <div className="main-menus">

                {renderSingleMenu({
                  to: '/insights',
                  iconNode: <AppstoreOutlined />,
                  title: 'Overview',
                })}

              {renderGroup({
                id: 'clients',
                iconSrc: '/icons/companies.svg',
                title: 'Clients',
                active: clientsActive,
                children: (
                  <>
                    {featureViewCompany && (
                      <NavLink to="/companies" className="sub-menu-item">
                      <span className="bullet" />
                      <div className="title">Companies</div>
                    </NavLink>)

                    }
                    

                    <NavLink to="/sites" className="sub-menu-item">
                      <span className="bullet" />
                      <div className="title">Sites</div>
                    </NavLink>
                  </>
                ),
              })}

              {renderSingleMenu({
                to: '/chambers',
                iconSrc: '/icons/chambers.svg',
                title: 'Chambers',
              })}

              {featureBuild &&
                renderGroup({
                  id: 'build',
                  iconSrc: '/icons/build.svg',
                  title: 'Build',
                  active: buildActive,
                  children: (
                    <>
                      <NavLink to="/catalog" className="sub-menu-item">
                        <span className="bullet" />
                        <div className="title">Products</div>
                      </NavLink>

                      <NavLink to="/projects" className="sub-menu-item">
                        <span className="bullet" />
                        <div className="title">Orders</div>
                      </NavLink>

                      <NavLink to="/production-planning" className="sub-menu-item">
                        <span className="bullet" />
                        <div className="title">Planning</div>
                      </NavLink>

                      <NavLink to="/production-timeline" className="sub-menu-item">
                        <span className="bullet" />
                        <div className="title">Production</div>
                      </NavLink>

                      <NavLink to="/contractors" className="sub-menu-item">
                        <span className="bullet" />
                        <div className="title">Contractors</div>
                      </NavLink>
                    </>
                  ),
                })}

              {renderGroup({
                id: 'reports',
                iconSrc: '/icons/reports.svg',
                title: 'Reports',
                active: reportsActive,
                children: (
                  <>
                    <NavLink to="/reports/serviced" className="sub-menu-item">
                      <span className="bullet" />
                      <div className="title">Service Reports</div>
                    </NavLink>

                    <NavLink to="/reports/after-service" className="sub-menu-item">
                      <span className="bullet" />
                      <div className="title">Service Summaries</div>
                    </NavLink>

                    <NavLink to="/reports/handover" className="sub-menu-item">
                      <span className="bullet" />
                      <div className="title">Handover Reports</div>
                    </NavLink>

                    <NavLink to="/reports/site-asr" className="sub-menu-item">
                      <span className="bullet" />
                      <div className="title">Site ASR</div>
                    </NavLink>
                  </>
                ),
              })}

              {renderSingleMenu({
                to: '/documents',
                iconSrc: '/icons/documents.svg',
                title: 'Documents',
              })}

              {featureUsers && renderSingleMenu({
                to: '/users',
                iconSrc: '/icons/users.svg',
                title: 'Users',
              })}
            </div>

            {isSidebarCollapsed ? (
              <Tooltip placement="right" title="Logout">
                <button className="menu-item logout" onClick={handleLogout} type="button">
                  <img alt="i" src="/icons/logout.svg" className="icon" />
                  <div className="title">
                    <span className="menu-text">Logout</span>
                  </div>
                </button>
              </Tooltip>
            ) : (
              <button className="menu-item logout" onClick={handleLogout} type="button">
                <img alt="i" src="/icons/logout.svg" className="icon" />
                <div className="title">
                  <span className="menu-text">Logout</span>
                </div>
              </button>
            )}
          </div>
        </aside>

        <div className="workspace d-flex f-col">
          <div className="header d-flex f-row jc-space-between">
            <div className="header-left d-flex ai-center">
              <button
                type="button"
                className="sidebar-toggle top-toggle"
                onClick={toggleSidebar}
                aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {isSidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              </button>
              <div className="breadcrumbs" aria-label="Breadcrumb">
                <span>MineARC</span>
                <span className="breadcrumb-divider">/</span>
                <span className="breadcrumb-current">{currentSection}</span>
              </div>
            </div>

            <div className="header-actions d-flex ai-center">
              <Popover
                placement="bottomRight"
                trigger={NOTIFICATIONS_ENABLED ? 'click' : []}
                overlayClassName="notification-popover"
                content={notificationContent}
                open={NOTIFICATIONS_ENABLED && isNotificationOpen}
                onOpenChange={handleNotificationOpenChange}
              >
                <button
                  type="button"
                  className="notification-btn"
                  aria-label="Notifications"
                  aria-disabled={!NOTIFICATIONS_ENABLED}
                  onClick={(e) => {
                    if (!NOTIFICATIONS_ENABLED) e.preventDefault();
                  }}
                >
                  <Badge count={NOTIFICATIONS_ENABLED ? unreadCount : 0} overflowCount={99} size="small">
                    <BellOutlined />
                  </Badge>
                </button>
              </Popover>

              <Popover
                title="User Account"
                placement="bottomRight"
                content={
                  <div>
                    <Link to="/profile">Edit Profile</Link>
                  </div>
                }
              >
                <div className="profile-dropdown d-flex ai-center jc-space-between">
                  <div className="text">
                    <div className="name fw-600">{userData.user_full_name}</div>
                    <div className="email fw-400">{formatRoleName(userRoleForLayout)}</div>
                  </div>
                  <Avatar
                    size={30}
                    icon={!hasProfilePicture ? <UserOutlined /> : undefined}
                    src={hasProfilePicture ? profilePicture : undefined}
                  />
                </div>
              </Popover>
            </div>
          </div>

        <main className={`content ${isSidebarCollapsed ? 'content-expanded' : ''}`}>{children}</main>
        </div>
      </div>
    </div>
  );
};

export default Layout;